import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useGetIdentity, useNotify } from 'react-admin';
import { authProxy } from './apiGateway';

type IdentityUser = {
    login?: string;
    fullName?: string;
    nickname?: string;
    name?: string;
};

const validatePasswordPolicy = (password: string) => {
    if (password.length < 10) {
        return 'Пароль должен содержать минимум 10 символов';
    }
    if (!/[A-Z]/.test(password)) {
        return 'Пароль должен содержать минимум 1 заглавную букву';
    }
    if (!/[a-z]/.test(password)) {
        return 'Пароль должен содержать минимум 1 строчную букву';
    }
    if (!/[0-9]/.test(password)) {
        return 'Пароль должен содержать минимум 1 цифру';
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
        return 'Пароль должен содержать минимум 1 спецсимвол';
    }
    return null;
};

const resolveApiErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message.trim()) {
        const jsonMatch = error.message.match(/\{.*\}$/);
        if (jsonMatch) {
            try {
                const parsed = JSON.parse(jsonMatch[0]) as { error?: unknown };
                if (typeof parsed.error === 'string' && parsed.error.trim()) {
                    return parsed.error;
                }
            } catch (_parseError) {
                // ignore parse error and return fallback below
            }
        }

        return error.message;
    }

    return fallback;
};

const randomInt = (maxExclusive: number) => {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return array[0] % maxExclusive;
    }

    return Math.floor(Math.random() * maxExclusive);
};

const generateStrongPassword = (length = 20) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const special = '!@#$%^&*()_+-=';
    const all = upper + lower + digits + special;
    const randomChar = (alphabet: string) => alphabet[randomInt(alphabet.length)];

    const seed = [
        randomChar(upper),
        randomChar(lower),
        randomChar(digits),
        randomChar(special)
    ];

    while (seed.length < length) {
        seed.push(randomChar(all));
    }

    for (let index = seed.length - 1; index > 0; index -= 1) {
        const randomIndex = randomInt(index + 1);
        const current = seed[index];
        seed[index] = seed[randomIndex];
        seed[randomIndex] = current;
    }

    return seed.join('');
};

export const ProfileSettingsPage = () => {
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as IdentityUser | null;
    const nickname = currentUser?.nickname || currentUser?.login || '—';
    const name = currentUser?.name || currentUser?.fullName || '—';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleGeneratePassword = () => {
        const generatedPassword = generateStrongPassword(20);
        setNewPassword(generatedPassword);
        setConfirmPassword(generatedPassword);
        notify('Новый пароль сгенерирован', { type: 'info' });
    };

    const handleChangePassword = async () => {
        if (saving) {
            return;
        }

        if (!newPassword.trim()) {
            notify('Введите новый пароль', { type: 'warning' });
            return;
        }
        if (newPassword !== confirmPassword) {
            notify('Подтверждение пароля не совпадает', { type: 'warning' });
            return;
        }

        const passwordValidationError = validatePasswordPolicy(newPassword);
        if (passwordValidationError) {
            notify(passwordValidationError, { type: 'warning' });
            return;
        }

        setSaving(true);
        try {
            await authProxy.changePassword({ newPassword });
            notify('Пароль успешно изменен', { type: 'info' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            notify(resolveApiErrorMessage(error, 'Не удалось изменить пароль'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (isPending) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Настройки профиля
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                    <Typography variant="h6">Профиль</Typography>
                    <TextField label="Никнейм" value={nickname} fullWidth disabled />
                    <TextField label="Имя" value={name} fullWidth disabled />
                    <Alert severity="info">
                        Для смены никнейма и имени обратитесь к администратору.
                    </Alert>
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Typography variant="h6">Смена пароля</Typography>
                    <TextField
                        label="Новый пароль"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        type={showNewPassword ? 'text' : 'password'}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() => setShowNewPassword((prev) => !prev)}
                                            onMouseDown={(event) => event.preventDefault()}
                                            aria-label={showNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                        >
                                            {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        fullWidth
                    />
                    <TextField
                        label="Подтверждение нового пароля"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        type={showConfirmPassword ? 'text' : 'password'}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            onMouseDown={(event) => event.preventDefault()}
                                            aria-label={showConfirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                        >
                                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        fullWidth
                    />
                    <Typography variant="body2" color="text.secondary">
                        Минимум 10 символов, 1 заглавная, 1 строчная, 1 цифра и 1 спецсимвол.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<AutoFixHighIcon />}
                            onClick={handleGeneratePassword}
                            disabled={saving}
                        >
                            Сгенерировать пароль
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleChangePassword}
                            disabled={saving}
                        >
                            Сменить пароль
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
};
